import { createContext, ReactNode, useContext, useMemo } from 'react';

import { FieldRule } from './types';

interface ScopeValue {
  policy: unknown;
  ctx: unknown;
  rule: (path: string) => FieldRule;
  form: unknown;
}

/**
 * One context for every policy, rather than one minted per feature.
 *
 * A feature therefore defines no provider component and no context of its own - it composes hooks
 * and wraps its subtree in `<PolicyScope>`.
 */
const PolicyScopeContext = createContext<ScopeValue | null>(null);
PolicyScopeContext.displayName = 'PolicyScope';

export interface PolicyScopeProps<TCtx extends object, TForm> {
  policy: { resolve: (path: any, ctx: TCtx) => FieldRule };
  ctx: TCtx;
  /** The feature's form instance. Opaque to the framework - never read here, only carried through
   *  to `usePolicyScope`'s consumers. */
  form?: TForm;
  children: ReactNode;
}

export const PolicyScope = <TCtx extends object, TForm = undefined>({ policy, ctx, form, children }: PolicyScopeProps<TCtx, TForm>) => {
  const rule = useMemo(() => (path: string) => policy.resolve(path, ctx), [policy, ctx]);
  const value = useMemo<ScopeValue>(() => ({ policy, ctx, rule, form }), [policy, ctx, rule, form]);

  return <PolicyScopeContext.Provider value={value}>{children}</PolicyScopeContext.Provider>;
};

/**
 * Reads the nearest scope.
 *
 * Passing `policy` does double duty: it supplies the type parameters a single shared context cannot
 * infer, and it asserts the nearest scope is the expected one - so a feature nested inside another
 * feature's scope fails loudly instead of resolving against the wrong registry.
 */
export const usePolicyScope = <TCtx extends object, TPath extends string = string, TForm = undefined>(
  policy: unknown,
): { ctx: TCtx; rule: (path: TPath) => FieldRule; form: TForm } => {
  const scope = useContext(PolicyScopeContext);

  if (!scope) {
    throw new Error('usePolicyScope must be used within a <PolicyScope>');
  }

  if (scope.policy !== policy) {
    throw new Error('usePolicyScope: the nearest <PolicyScope> was opened with a different policy');
  }

  return { ctx: scope.ctx as TCtx, rule: scope.rule as (path: TPath) => FieldRule, form: scope.form as TForm };
};
